import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
import moment from 'moment';
import style from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import Loading from '@components-clean/core/loading/loading';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { Observation } from '@interfaces-clean/observations.interface';
import { EditForm } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation.interface';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { getStudyMembers, getStudyMembersVariables } from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';

interface EditObservationProps {
    onClose: () => void;
    onSubmit: (data: EditForm) => void;
    studyRef: string;
    observation: Observation;
}

const COMPONENT_NAME = 'edit-study-observation-dialog';

const EditStudyObservationDialog = ({ onClose, onSubmit, studyRef, observation }: EditObservationProps) => {
    const { t } = useTranslation('cm');

    const [studyMembers, setStudyMembers] = useState<
        ({ fullName: string; ref: string; groupedBy: string; disabled: boolean; active: boolean } | null)[] | undefined
    >([]);
    const { data: studyMembersData, loading: loadingStudyMembers } = useQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS, {
        variables: { studyId: studyRef },
    });

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
        reset,
    } = useForm<EditForm>({
        mode: 'all',
        defaultValues: {
            owner: null,
            observation: observation.observation ?? '',
            rootCause: observation.root_cause ?? '',
            comments: observation.comments ?? '',
            closedDate: observation.closed_date ? new Date(observation.closed_date) : null,
            observationDate: observation.observation_date ? new Date(observation.observation_date) : null,
        },
    });

    useEffect(() => {
        if (studyMembersData?.studyMembers) {
            const studyUsers = studyMembersData.studyMembers
                .filter((y) => y)
                .sort((a, b) => {
                    if (a?.active !== b?.active) {
                        return a?.active ? -1 : 1;
                    }

                    const aName = `${a?.firstName?.toLowerCase() ?? ''} ${a?.lastName?.toLowerCase() ?? ''}`;
                    const bName = `${b?.firstName?.toLowerCase() ?? ''} ${b?.lastName?.toLowerCase() ?? ''}`;
                    return aName.localeCompare(bName);
                })
                .map((data) => ({
                    fullName: `${data?.firstName} ${data?.lastName}`,
                    ref: (data?.id as string) ?? '',
                    groupedBy: data?.active ? 'Active' : 'Inactive',
                    disabled: observation.owner_name
                        ? observation.owner_name.toLowerCase() ===
                          `${data?.firstName?.toLowerCase()} ${data?.lastName?.toLowerCase()}`
                            ? false
                            : !data?.active
                        : !data?.active,
                    active: data?.active ?? false,
                }));

            if (observation.owner_name) {
                const currentOwner = studyUsers?.find((user) => user.fullName === observation.owner_name) ?? null;
                if (currentOwner)
                    reset((currentDefaults) => {
                        return { ...currentDefaults, owner: currentOwner };
                    });
            }
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setStudyMembers(studyUsers);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyMembersData?.studyMembers, observation?.owner_name]);

    const dialogTitle = `${t('common:edit')} ${t('observationDialog.studyTitle')}: ${observation.record_id}`;

    const primaryBtnLabel = t('common:update');
    const createdBy = `${moment(observation?.created_date).format('DD-MMM-YYYY')} by ${observation?.created_by}`;
    const updatedBy = `${moment(observation?.updated_date).format('DD-MMM-YYYY')} by ${observation?.updated_by}`;

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    if (loadingStudyMembers) return <Loading />;

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    return (
        <DraggableDialog
            className={cn(style.kWindowContent)}
            dataTestId="edit-study-observation-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            createdUpdatedInfo={<CreatedUpdatedInfo created={createdBy} updated={updatedBy} />}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            initialHeight={645}
            initialWidth={522}
            bodyPadding="none"
            createdUpdatedBackgroundColour="gray"
        >
            <div className="k-w-full k-h-full k-d-flex-col">
                <div>
                    <div className="k-py-sm">
                        <div className={cn(style.divider, 'k-shrink-0 k-d-flex')}>
                            <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center k-gap-2">
                                <div>{t('observationDialog.numOfReviews')}</div>
                                <div className={cn(style.editSiteObservationTopInfoLabel)}>{observation.reviews}</div>
                            </div>
                            <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center k-gap-2">
                                <div>{t('observationDialog.numOfOpenActions')}</div>
                                <div className={cn(style.editSiteObservationTopInfoLabel)}>
                                    {observation.open_actions}
                                </div>
                            </div>
                            <div className="k-flex k-justify-content-center k-flex-row k-d-flex k-align-items-center k-gap-2">
                                <div>{t('observationDialog.recentAction')}</div>
                                <div className={cn(style.editSiteObservationTopInfoLabel)}>
                                    {stringDateWithoutTimeFromUtc(observation.recent_action, 'DD-MMM-YYYY') ?? 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={cn(style.editObservationInfo, 'k-pt-md k-px-md')}>
                        <div className={cn(style.divider, 'k-shrink-0 k-d-flex')}>
                            <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2 ">
                                <div className={cn(style.editObservationInfoLabel, 'k-font-medium')}>
                                    {t('observationDialog.status')}
                                </div>
                                <div className={cn(style.editSiteObservationTopInfoLabel)}>{observation.status}</div>
                            </div>
                            <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2">
                                <div className="k-font-medium k-pr-md">
                                    {t('observationDialog.datasetOpenedOnDate')}
                                </div>
                                <div className={cn(style.editSiteObservationTopInfoLabel)}>
                                    {stringDateWithoutTimeFromUtc(observation.dataset_opened_on, 'DD-MMM-YYYY')}
                                </div>
                            </div>
                        </div>
                        <div className="k-shrink-0 k-d-flex k-justify-content-left k-align-items-center">
                            <div className={cn(style.editObservationInfoLabel, 'k-font-medium')}>
                                {t('observationDialog.panel')}
                            </div>
                            <div className={cn(style.editSiteObservationTopInfoLabel, 'k-d-flex k-align-items-center')}>
                                {observation.panel ?? 'N/A'}
                                {observation?.panel_active === 'No' && (
                                    <div className={cn(style.inactiveIcon, 'k-pl-sm')}>
                                        <InactiveIcon
                                            toolTipText={t('common:inactiveIcon')}
                                            dataTestId="inactive-icon"
                                            tooltipPosition="bottom"
                                            className="k-mr-2"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="k-shrink-0 k-d-flex k-justify-content-left k-align-items-center">
                            <div className="k-pr-md k-font-medium">{t('observationDialog.associationLabel')}</div>
                            <div className={cn(style.editSiteObservationTopInfoLabel, 'k-d-flex')}>
                                {observation.association}
                                {observation?.association_active === 'No' && (
                                    <div className={cn(style.inactiveIcon, 'k-pl-sm')}>
                                        <InactiveIcon
                                            toolTipText={t('common:inactiveIcon')}
                                            dataTestId="inactive-icon"
                                            tooltipPosition="bottom"
                                            className="k-mr-2"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="k-shrink-0 k-d-flex k-justify-content-left k-align-items-center ">
                            <div className={cn(style.editObservationInfoLabel, 'k-font-medium')}>
                                {t('observationDialog.decision')}
                            </div>
                            <div className={cn(style.editSiteObservationTopInfoLabel, 'k-d-flex')}>
                                {observation.decision ?? 'N/A'}
                                {observation?.decision_active === 'No' && (
                                    <div className={cn(style.inactiveIcon, 'k-pl-sm')}>
                                        <InactiveIcon
                                            toolTipText={t('common:inactiveIcon')}
                                            dataTestId="inactive-icon"
                                            tooltipPosition="bottom"
                                            className="k-mr-2"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <form id="edit-site-observation-form" className="k-mt-md k-w-full k-pl-3 k-pr-3">
                        <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                            <BasicDropDownController
                                control={control}
                                name="owner"
                                data-testid="field-select-owner"
                                data={studyMembers ?? []}
                                textField="fullName"
                                groupField="groupedBy"
                                dataItemKey="ref"
                                label={t('observationDialog.owner')}
                                loading={loadingStudyMembers}
                                disabled={loadingStudyMembers}
                                filterable
                                allowClear
                                activeField="active"
                                readOnly={isSubmitting}
                            />
                        </div>
                        <div className="k-d-flex k-justify-content-start k-pb-md k-gap-4">
                            <div className="!k-basis-1/2">
                                <DatePickerController
                                    control={control}
                                    name="observationDate"
                                    data-testid="datepicker-observationDate"
                                    label={t('observationDialog.observationDate')}
                                    fillMode="solid"
                                    required
                                    readonly={isSubmitting}
                                />
                            </div>
                            <div className="!k-basis-1/2">
                                <DatePickerController
                                    control={control}
                                    name="closedDate"
                                    data-testid="datepicker-closedDate"
                                    label={t('observationDialog.closedDate')}
                                    fillMode="solid"
                                    readonly={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                            <TextAreaController
                                className={style.textAreaOverrides}
                                maxLength={3000}
                                control={control}
                                name="observation"
                                required
                                data-testid="field-select-observation"
                                label={t('observationDialog.observation')}
                                readOnly={isSubmitting}
                            />
                        </div>
                        <div className="k-pb-md k-input-md">
                            <TextAreaDropdownController
                                maxLength={3000}
                                className={style.textAreaOverrides}
                                control={control}
                                name="rootCause"
                                data-testid="field-select-rootCause"
                                label={t('observationDialog.rootCause')}
                                rows={4}
                                expanded={observation.root_cause !== null && observation.root_cause !== ''}
                                readOnly={isSubmitting}
                            />
                        </div>
                        <div className="k-pb-md k-input-md">
                            <TextAreaDropdownController
                                maxLength={3000}
                                control={control}
                                className={cn(style.textAreaOverrides)}
                                name="comments"
                                data-testid="field-select-comments"
                                label={t('observationDialog.comments')}
                                rows={4}
                                expanded={observation.comments !== null && observation.comments !== ''}
                                readOnly={isSubmitting}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </DraggableDialog>
    );
};

export default EditStudyObservationDialog;
